import { Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { useNotifications } from 'providers/NotificationProvider'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import theme from 'lib/theme'
import { Notification } from 'types/notification'
import { format } from 'date-fns'
import { NotificationIcon } from 'components/NotificationIcon/NotificationIcon'

interface NotificationModalProps {
  open: boolean
  onClose: () => void
}

export const NotificationModal = ({
  open,
  onClose,
}: NotificationModalProps) => {
  const {
    notifications,
    clearNotification,
    clearAllNotifications,
    notificationsEnabled,
    toggleNotifications,
  } = useNotifications()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleNotificationClick = (notification: Notification) => {
    clearNotification(notification.id)
    onClose()
    navigate(`/order/${notification.parentOrderId}/info`)
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        'data-testid': 'notification-modal',
        sx: {
          width: {
            xs: '100%',
            sm: '400px',
            md: '480px',
          },
          padding: 0,
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
        }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'white',
            zIndex: 10,
            position: 'sticky',
            top: 0,
            mb: 4,
          }}>
          <Typography
            variant='h2'
            sx={{
              fontWeight: 'bold',
            }}>
            {t('notifications')}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Notification List */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            mx: -2,
            px: 2,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.neutral.grey6,
              borderRadius: '3px',
            },
          }}>
          {!notificationsEnabled && (
            <Box
              sx={{
                padding: theme.spacing(6, 0),
                textAlign: 'center',
              }}>
              <NotificationsOffIcon
                sx={{
                  fontSize: 64,
                  color: theme.palette.text.disabled,
                  marginBottom: theme.spacing(2),
                }}
              />
              <Typography variant='h6' color='textSecondary' gutterBottom>
                {t('notifications_off')}
              </Typography>
              <Button
                onClick={toggleNotifications}
                variant='contained'
                sx={{
                  mt: 3,
                  borderRadius: '20px',
                  textTransform: 'none',
                }}>
                {t('turn_on_notifications')}
              </Button>
            </Box>
          )}
          {notificationsEnabled && notifications.length === 0 && (
            <Box
              sx={{
                padding: theme.spacing(6, 0),
                textAlign: 'center',
              }}>
              <NotificationsIcon
                sx={{
                  fontSize: 64,
                  color: theme.palette.text.disabled,
                  marginBottom: theme.spacing(2),
                }}
              />
              <Typography variant='h6' color='textSecondary' gutterBottom>
                {t('no_notifications')}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {t('all_caught_up')}
              </Typography>
            </Box>
          )}
          {notificationsEnabled &&
            notifications.length > 0 &&
            notifications.map((notification) => (
              <Box
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  padding: theme.spacing(2),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 24,
                    pt: 0.5,
                  }}>
                  <NotificationIcon
                    type={notification.event}
                    size={14}
                    colour={false}
                  />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                    sx={{
                      marginBottom: theme.spacing(0.5),
                      wordBreak: 'break-word',
                    }}>
                    {t(`notification_${notification.event}`, {
                      order_id: notification.visibleId,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    pt: 0.5,
                  }}>
                  <Typography variant='caption' color='textSecondary'>
                    {format(notification.timestamp, 'HH:mm')}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Box>

        {/* Footer Actions */}
        {notificationsEnabled && notifications.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              pt: 2,
              mt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}>
            <Button
              onClick={() => {
                clearAllNotifications()
                onClose()
              }}
              size='medium'
              variant='outlined'
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
              }}>
              {t('clear_all')}
            </Button>
          </Box>
        )}

        {/* Turn Notifications Off Button */}
        {notificationsEnabled && (
          <Box
            sx={{
              pt: notifications.length > 0 ? 2 : 0,
              textAlign: 'center',
            }}>
            <Button
              onClick={() => {
                toggleNotifications()
                onClose()
              }}
              variant='text'
              color='error'
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}>
              {t('turn_off_notifications')}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}
